import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Nav } from 'react-bootstrap';
import { useMutation } from '@apollo/client/react';
import { Notify } from 'notiflix';
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react';
import 'survey-creator-core/survey-creator-core.min.css';
import { CREATE_EVENT, UPDATE_EVENT } from 'helper/graphql/mutations';

const EMPTY_FORM = {
	title: '',
	eventType: '',
	description: '',
	sessionDatetime: '',
	durationMinutes: 60,
	joinLink: '',
	price: 0,
	physicalPrice: 0,
	maxParticipants: 0,
	isActive: true,
	feedbackFormSchema: null,
};

const EMPTY_SPEAKER = { name: '', title: '' };

const CREATOR_OPTIONS = {
	showLogicTab: false,
	showTranslationTab: false,
	showThemeTab: false,
	showJSONEditorTab: false,
	isAutoSave: true,
};

// Parse speakersData JSON string → array of {name, title}
const parseSpeakers = (raw) => {
	if (!raw) return null;
	try {
		const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
	} catch {
		return null;
	}
};

const CreateEditEventModal = ({ show, onHide, event, onSaved, lastEventSchema }) => {
	const isEdit = !!event;
	const [form, setForm] = useState(EMPTY_FORM);
	const [speakers, setSpeakers] = useState([{ ...EMPTY_SPEAKER }]);
	const [bannerFile, setBannerFile] = useState(null);
	const [bannerPreview, setBannerPreview] = useState(null);
	const [activeTab, setActiveTab] = useState('details');
	const bannerInputRef = useRef(null);

	// SurveyJS Creator instance — created once, reused across renders
	const [creator] = useState(() => {
		const c = new SurveyCreator(CREATOR_OPTIONS);
		c.isAutoSave = true;
		return c;
	});

	// Populate form when editing
	useEffect(() => {
		if (event) {
			setForm({
				title: event.title || '',
				eventType: event.eventType || '',
				description: event.description || '',
				sessionDatetime: event.sessionDatetime
					? event.sessionDatetime.slice(0, 16)
					: '',
				durationMinutes: event.durationMinutes ?? 60,
				joinLink: event.joinLink || '',
				price: event.price ?? 0,
				physicalPrice: event.physicalPrice ?? 0,
				maxParticipants: event.maxParticipants ?? 0,
				isActive: event.isActive ?? true,
				feedbackFormSchema: event.feedbackFormSchema || null,
			});
			// Speakers — prefer speakersData array, fallback to legacy single fields
			const parsed = parseSpeakers(event.speakersData);
			if (parsed) {
				setSpeakers(parsed);
			} else if (event.speakerName) {
				setSpeakers([{ name: event.speakerName, title: event.speakerTitle || '' }]);
			} else {
				setSpeakers([{ ...EMPTY_SPEAKER }]);
			}
			// Load existing feedback schema into creator
			if (event.feedbackFormSchema) {
				try {
					const raw = event.feedbackFormSchema;
					creator.JSON = typeof raw === 'string' ? JSON.parse(raw) : raw;
				} catch {
					creator.JSON = {};
				}
			} else {
				creator.JSON = {};
			}
			// Reset banner
			setBannerFile(null);
			setBannerPreview(event.bannerImage || null);
		} else {
			setForm(EMPTY_FORM);
			setSpeakers([{ ...EMPTY_SPEAKER }]);
			setBannerFile(null);
			setBannerPreview(null);
			creator.JSON = {};
		}
		setActiveTab('details');
	}, [event, show]);

	useLayoutEffect(() => {
		const banner = document.querySelector('div.svc-creator__banner');
		if (banner) banner.style.display = 'none';
	});

	const [onCreate, { loading: creating }] = useMutation(CREATE_EVENT);
	const [onUpdate, { loading: updating }] = useMutation(UPDATE_EVENT);
	const loading = creating || updating;

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};

	const handleBannerChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setBannerFile(file);
		setBannerPreview(URL.createObjectURL(file));
	};

	// Speakers management
	const updateSpeaker = (idx, field, value) => {
		setSpeakers((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
	};
	const addSpeaker = () => setSpeakers((prev) => [...prev, { ...EMPTY_SPEAKER }]);
	const removeSpeaker = (idx) => setSpeakers((prev) => prev.filter((_, i) => i !== idx));

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			// Pull latest schema from creator
			const schema = creator.JSON;
			const hasSchema = schema && Object.keys(schema).length > 0;

			// Filter out empty speakers
			const validSpeakers = speakers.filter((s) => s.name.trim());

			// Gateway expects JSON strings for speakersData and feedbackFormSchema
			const speakersDataStr = validSpeakers.length > 0 ? JSON.stringify(validSpeakers) : null;
			const feedbackStr = hasSchema ? JSON.stringify(schema) : null;

			// Keep legacy speakerName/speakerTitle as first speaker for backward compat
			const primarySpeaker = validSpeakers[0] || {};

			const variables = {
				...form,
				durationMinutes: parseInt(form.durationMinutes, 10) || 60,
				maxParticipants: parseInt(form.maxParticipants, 10) || 0,
				price: parseInt(form.price, 10) || 0,
				physicalPrice: parseInt(form.physicalPrice, 10) || 0,
				sessionDatetime: new Date(form.sessionDatetime).toISOString(),
				speakerName: primarySpeaker.name || null,
				speakerTitle: primarySpeaker.title || null,
				speakersData: speakersDataStr,
				feedbackFormSchema: feedbackStr,
				...(bannerFile ? { bannerImage: bannerFile } : {}),
			};

			if (isEdit) {
				await onUpdate({ variables: { guid: event.guid, ...variables } });
				Notify.success('Event updated successfully!');
			} else {
				await onCreate({ variables });
				Notify.success('Event created successfully!');
			}
			onSaved();
			onHide();
		} catch (err) {
			Notify.failure(err?.message || 'Something went wrong.');
		}
	};

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton className="cem-header">
				<Modal.Title className="cem-title">
					<i className={`fe ${isEdit ? 'fe-edit-2' : 'fe-plus-circle'} me-2`}></i>
					{isEdit ? 'Edit Event' : 'Create New Event'}
				</Modal.Title>
			</Modal.Header>

			<Form onSubmit={handleSubmit}>
				<div className="cem-tabs-bar">
					<Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="cem-nav">
						<Nav.Item>
							<Nav.Link eventKey="details" className="cem-tab">
								<i className="fe fe-info me-2"></i>Event Details
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="feedback" className="cem-tab">
								<i className="fe fe-file-text me-2"></i>Feedback Form
							</Nav.Link>
						</Nav.Item>
					</Nav>
				</div>

				<Modal.Body className="cem-body p-0">
				{activeTab === 'details' && (
				<div className="p-4">
				<Row className="g-3">
						{/* Banner Image */}
						<Col md={12}>
							<Form.Group>
								<Form.Label className="cem-label">Event Poster</Form.Label>
								<div className="cem-banner-wrap">
									{bannerPreview ? (
										<div className="cem-banner-preview">
											<img src={bannerPreview} alt="Poster preview" className="cem-banner-img" />
											<button
												type="button"
												className="cem-banner-remove"
												onClick={() => { setBannerFile(null); setBannerPreview(null); if (bannerInputRef.current) bannerInputRef.current.value = ''; }}
											>
												<i className="fe fe-x"></i>
											</button>
										</div>
									) : (
										<button
											type="button"
											className="cem-banner-placeholder"
											onClick={() => bannerInputRef.current?.click()}
										>
											<i className="fe fe-image"></i>
											<span>Upload poster</span>
										</button>
									)}
									<input
										ref={bannerInputRef}
										type="file"
										accept="image/*"
										className="d-none"
										onChange={handleBannerChange}
									/>
									<div className="cem-banner-side-info">
										<p className="cem-banner-hint">Portrait format works best (e.g. 600 × 900 px)</p>
										{bannerPreview && (
											<button
												type="button"
												className="cem-banner-change"
												onClick={() => bannerInputRef.current?.click()}
											>
												<i className="fe fe-refresh-cw me-1"></i>Change poster
											</button>
										)}
									</div>
								</div>
							</Form.Group>
						</Col>

						{/* Title */}
						<Col md={8}>
							<Form.Group>
								<Form.Label className="cem-label">
									Title <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="title"
									value={form.title}
									onChange={handleChange}
									placeholder="e.g. Monthly X Space — Arthritis Q&A"
									className="cem-input"
									required
								/>
							</Form.Group>
						</Col>

						{/* Event Type */}
						<Col md={4}>
							<Form.Group>
								<Form.Label className="cem-label">
									Event Type <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="eventType"
									value={form.eventType}
									onChange={handleChange}
									placeholder="e.g. twitter_space"
									className="cem-input"
									required
								/>
								<Form.Text className="text-muted">Free text — any value</Form.Text>
							</Form.Group>
						</Col>

						{/* Description */}
						<Col md={12}>
							<Form.Group>
								<Form.Label className="cem-label">Description</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									name="description"
									value={form.description}
									onChange={handleChange}
									placeholder="What will be discussed in this session?"
									className="cem-input"
								/>
							</Form.Group>
						</Col>

						{/* Date/Time */}
						<Col md={6}>
							<Form.Group>
								<Form.Label className="cem-label">
									Date &amp; Time <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="datetime-local"
									name="sessionDatetime"
									value={form.sessionDatetime}
									onChange={handleChange}
									className="cem-input"
									required
								/>
							</Form.Group>
						</Col>

						{/* Duration */}
						<Col md={3}>
							<Form.Group>
								<Form.Label className="cem-label">Duration (min)</Form.Label>
								<Form.Control
									type="number"
									name="durationMinutes"
									value={form.durationMinutes}
									onChange={handleChange}
									min={1}
									className="cem-input"
								/>
							</Form.Group>
						</Col>

						{/* Max Participants */}
						<Col md={3}>
							<Form.Group>
								<Form.Label className="cem-label">Max Participants</Form.Label>
								<Form.Control
									type="number"
									name="maxParticipants"
									value={form.maxParticipants}
									onChange={handleChange}
									min={0}
									className="cem-input"
								/>
								<Form.Text className="text-muted">0 = unlimited</Form.Text>
							</Form.Group>
						</Col>

						{/* Join Link */}
						<Col md={6}>
							<Form.Group>
								<Form.Label className="cem-label">
									Join Link <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="url"
									name="joinLink"
									value={form.joinLink}
									onChange={handleChange}
									placeholder="https://twitter.com/i/spaces/..."
									className="cem-input"
									required
								/>
								<Form.Text className="text-muted">
									This link is emailed to participants upon registration
								</Form.Text>
							</Form.Group>
						</Col>

						{/* Virtual Price */}
						<Col md={3}>
							<Form.Group>
								<Form.Label className="cem-label">Virtual Price (Ksh)</Form.Label>
								<Form.Control
									type="number"
									name="price"
									value={form.price}
									onChange={handleChange}
									min={0}
									className="cem-input"
								/>
								<Form.Text className="text-muted">0 = free</Form.Text>
							</Form.Group>
						</Col>

						{/* Physical Price */}
						<Col md={3}>
							<Form.Group>
								<Form.Label className="cem-label">In-Person Price (Ksh)</Form.Label>
								<Form.Control
									type="number"
									name="physicalPrice"
									value={form.physicalPrice}
									onChange={handleChange}
									min={0}
									className="cem-input"
								/>
								<Form.Text className="text-muted">0 = free</Form.Text>
							</Form.Group>
						</Col>

						{/* Speakers */}
						<Col md={12}>
							<Form.Label className="cem-label d-flex align-items-center justify-content-between">
								<span>Speakers</span>
								<button type="button" className="cem-add-speaker-btn" onClick={addSpeaker}>
									<i className="fe fe-plus me-1"></i>Add Speaker
								</button>
							</Form.Label>
							<div className="cem-speakers-list">
								{speakers.map((s, idx) => (
									<div key={idx} className="cem-speaker-row">
										<div className="cem-speaker-num">{idx + 1}</div>
										<Form.Control
											value={s.name}
											onChange={(e) => updateSpeaker(idx, 'name', e.target.value)}
											placeholder="Speaker name (e.g. Dr. Jane Smith)"
											className="cem-input flex-1"
										/>
										<Form.Control
											value={s.title}
											onChange={(e) => updateSpeaker(idx, 'title', e.target.value)}
											placeholder="Title / role (e.g. Rheumatologist, UCT)"
											className="cem-input flex-1"
										/>
										{speakers.length > 1 && (
											<button type="button" className="cem-remove-speaker" onClick={() => removeSpeaker(idx)}>
												<i className="fe fe-trash-2"></i>
											</button>
										)}
									</div>
								))}
							</div>
						</Col>

						{/* Active toggle */}
						<Col md={12}>
							<Form.Check
								type="switch"
								id="isActive-switch"
								name="isActive"
								label="Publish event (visible to users)"
								checked={form.isActive}
								onChange={handleChange}
								className="cem-switch"
							/>
						</Col>
					</Row>
				</div>
				)}

				{activeTab === 'feedback' && (
					<div className="cem-creator-wrap">
						<div className="cem-creator-hint">
							<i className="fe fe-info me-2"></i>
							Design the post-session feedback form. Users complete this after the event to trigger certificate generation.
							{!isEdit && lastEventSchema && (
								<button
									type="button"
									className="cem-prefill-btn ms-auto"
									onClick={() => { creator.JSON = lastEventSchema; }}
								>
									<i className="fe fe-copy me-1"></i>Copy from last event
								</button>
							)}
						</div>
						<SurveyCreatorComponent creator={creator} />
					</div>
				)}
				</Modal.Body>

				<Modal.Footer className="cem-footer">
					<Button variant="light" onClick={onHide} disabled={loading} className="cem-cancel-btn">
						Cancel
					</Button>
					<Button type="submit" className="cem-save-btn" disabled={loading}>
						{loading ? (
							<><Spinner size="sm" className="me-2" />{isEdit ? 'Saving...' : 'Creating...'}</>
						) : (
							<><i className={`fe ${isEdit ? 'fe-save' : 'fe-plus'} me-2`}></i>
							{isEdit ? 'Save Changes' : 'Create Event'}</>
						)}
					</Button>
				</Modal.Footer>
			</Form>

			<style>{`
				.cem-tabs-bar {
					border-bottom: 2px solid #e2e8f0;
					background: #f8fafc;
					padding: 0 1.5rem;
				}
				.cem-nav .cem-tab {
					font-size: 0.875rem;
					font-weight: 600;
					color: #64748b;
					border: none;
					border-bottom: 2px solid transparent;
					margin-bottom: -2px;
					padding: 0.75rem 1rem;
				}
				.cem-nav .cem-tab.active {
					color: #F68521;
					border-bottom-color: #F68521;
					background: transparent;
				}
				.cem-creator-wrap {
					height: 600px;
					display: flex;
					flex-direction: column;
				}
				.cem-creator-hint {
					padding: 0.625rem 1rem;
					background: rgba(29,158,176,0.06);
					border-bottom: 1px solid #e2e8f0;
					font-size: 0.8125rem;
					color: #475569;
					display: flex;
					align-items: center;
				}
				.cem-creator-wrap .svc-creator { flex: 1; min-height: 0; }
				.cem-prefill-btn {
					background: none; border: 1.5px solid #1d9eb0; color: #1d9eb0;
					border-radius: 6px; padding: 3px 10px; font-size: 0.75rem;
					font-weight: 600; cursor: pointer; display: inline-flex; align-items: center;
					white-space: nowrap;
				}
				.cem-prefill-btn:hover { background: rgba(29,158,176,0.08); }
				.cem-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					border-bottom: 2px solid #e2e8f0;
					padding: 1.25rem 1.5rem;
				}
				.cem-title {
					font-size: 1.0625rem;
					font-weight: 700;
					color: #1e293b;
					display: flex;
					align-items: center;
				}
				.cem-body { padding: 1.5rem; }
				.cem-label {
					font-size: 0.875rem;
					font-weight: 600;
					color: #374151;
					margin-bottom: 0.375rem;
					width: 100%;
				}
				.cem-input {
					border: 2px solid #e2e8f0;
					border-radius: 10px;
					padding: 0.6rem 1rem;
					font-size: 0.9rem;
					transition: border-color 0.2s;
				}
				.cem-input:focus {
					border-color: #F68521;
					box-shadow: 0 0 0 3px rgba(246,133,33,0.1);
				}

				/* Poster upload */
				.cem-banner-wrap { display: flex; flex-direction: row; align-items: flex-start; gap: 1rem; }
				.cem-banner-preview {
					position: relative;
					width: 120px;
					min-width: 120px;
					aspect-ratio: 2/3;
					border-radius: 10px;
					overflow: hidden;
					border: 2px solid #e2e8f0;
					flex-shrink: 0;
				}
				.cem-banner-img {
					width: 100%; height: 100%; object-fit: cover; display: block;
				}
				.cem-banner-remove {
					position: absolute; top: 6px; right: 6px;
					background: rgba(0,0,0,0.55); border: none; color: white;
					width: 26px; height: 26px; border-radius: 50%;
					display: flex; align-items: center; justify-content: center;
					cursor: pointer; font-size: 0.7rem;
				}
				.cem-banner-placeholder {
					width: 120px; min-width: 120px;
					aspect-ratio: 2/3;
					border: 2px dashed #cbd5e1;
					border-radius: 10px; background: #f8fafc; color: #94a3b8;
					font-size: 0.8125rem; font-weight: 600; cursor: pointer;
					display: flex; flex-direction: column; align-items: center; justify-content: center;
					gap: 0.375rem;
					transition: all 0.2s;
					text-align: center;
					padding: 0.5rem;
				}
				.cem-banner-placeholder:hover { border-color: #F68521; color: #F68521; background: rgba(246,133,33,0.04); }
				.cem-banner-change {
					background: none; border: 1.5px solid #e2e8f0; border-radius: 8px;
					color: #64748b; font-size: 0.75rem; font-weight: 600; padding: 4px 10px;
					cursor: pointer; display: inline-flex; align-items: center;
					margin-top: auto;
				}
				.cem-banner-change:hover { border-color: #1d9eb0; color: #1d9eb0; }
				.cem-banner-side-info {
					display: flex; flex-direction: column; justify-content: flex-end;
					gap: 0.5rem; padding-bottom: 0.25rem;
				}
				.cem-banner-hint {
					font-size: 0.8rem; color: #94a3b8; margin: 0; line-height: 1.4;
				}

				/* Speakers */
				.cem-speakers-list { display: flex; flex-direction: column; gap: 0.625rem; }
				.cem-speaker-row {
					display: flex; align-items: center; gap: 0.625rem;
				}
				.cem-speaker-num {
					width: 24px; height: 24px; border-radius: 50%;
					background: rgba(246,133,33,0.12); color: #F68521;
					font-size: 0.75rem; font-weight: 700;
					display: flex; align-items: center; justify-content: center;
					flex-shrink: 0;
				}
				.cem-speaker-row .cem-input { flex: 1; }
				.cem-remove-speaker {
					background: none; border: 1.5px solid #fee2e2; color: #ef4444;
					border-radius: 8px; padding: 6px 8px; cursor: pointer;
					font-size: 0.75rem; flex-shrink: 0;
					display: flex; align-items: center; justify-content: center;
				}
				.cem-remove-speaker:hover { background: #fee2e2; }
				.cem-add-speaker-btn {
					background: none; border: 1.5px solid #1d9eb0; color: #1d9eb0;
					border-radius: 6px; padding: 3px 10px; font-size: 0.75rem;
					font-weight: 600; cursor: pointer; display: inline-flex; align-items: center;
					white-space: nowrap;
				}
				.cem-add-speaker-btn:hover { background: rgba(29,158,176,0.08); }

				.cem-switch { font-size: 0.9rem; font-weight: 500; }
				.cem-footer {
					border-top: 1px solid #e2e8f0;
					padding: 1rem 1.5rem;
					gap: 0.75rem;
				}
				.cem-cancel-btn {
					border: 1.5px solid #e2e8f0;
					color: #64748b;
					font-weight: 600;
					border-radius: 8px;
				}
				.cem-save-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					color: white;
					font-weight: 600;
					border-radius: 8px;
					padding: 0.5rem 1.5rem;
					display: flex;
					align-items: center;
				}
				.cem-save-btn:hover:not(:disabled) {
					opacity: 0.9;
					color: white;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
				}
				.cem-save-btn:disabled {
					opacity: 0.65;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white;
				}
			`}</style>
		</Modal>
	);
};

export default CreateEditEventModal;
